"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Board = void 0;
const buildings_1 = require("./config/buildings");
const electricity_1 = require("./layers/electricity");
const production_1 = require("./layers/production");
const transport_1 = require("./layers/transport");
// Deterministic ID generator — per-board counter, no Date.now()
// This ensures identical event sequences produce identical IDs (determinism requirement)
class Board {
    genId(prefix) {
        return `${prefix}-${this.state.boardId}-${this.idCounter++}`;
    }
    constructor(boardId, width, height) {
        this.idCounter = 0;
        const grid = [];
        for (let y = 0; y < height; y++) {
            grid.push('#'.repeat(width));
        }
        this.state = {
            tick: 0,
            boardId,
            grid: {
                width,
                height,
                layout: grid,
            },
            buildings: [],
            minibots: [],
            layers: {
                electricity: {
                    supply: 0,
                    demand: 0,
                    stored: 0,
                    storageCapacity: 0,
                    efficiencyModifiers: {},
                },
                production: {
                    buildingProgress: {},
                },
                transport: {
                    minibots: [],
                },
            },
            pendingEvents: [],
        };
        // Layers execute in fixed order: electricity → production → transport
        this.layers = [electricity_1.electricityLayer, production_1.productionLayer, transport_1.transportLayer];
    }
    // Apply a player event to this board's state
    applyEvent(event) {
        switch (event.type) {
            case 'BUILDING_PLACED':
                this.applyBuildPlaced(event);
                break;
            case 'BUILDING_UPGRADED':
                this.applyBuildUpgraded(event);
                break;
            case 'BUILDING_DEMOLISHED':
                this.applyBuildDemolished(event);
                break;
            case 'BUILDING_TOGGLED':
                this.applyBuildToggled(event);
                break;
            case 'HULL_EXPANDED':
                this.applyHullExpanded(event);
                break;
            case 'MINIBOT_BUILT':
                this.applyMinibotBuilt(event);
                break;
            case 'CROSS_BOARD_EVENT':
                this.applyCrossBoardEvent(event);
                break;
        }
    }
    applyBuildPlaced(event) {
        const { buildingName, x, y } = event.payload;
        const config = buildings_1.allBuildings[buildingName];
        if (!config)
            return;
        const building = {
            id: this.genId('b'),
            name: buildingName,
            level: 0,
            x,
            y,
            width: config.width,
            height: config.height,
            isEnabled: true,
            contains: {},
        };
        // Mark grid cells as occupied ('b' = building)
        for (let dy = 0; dy < config.height; dy++) {
            for (let dx = 0; dx < config.width; dx++) {
                const row = this.state.grid.layout[y + dy];
                if (row) {
                    this.state.grid.layout[y + dy] =
                        row.substring(0, x + dx) + 'b' + row.substring(x + dx + 1);
                }
            }
        }
        this.state.buildings.push(building);
    }
    applyBuildUpgraded(event) {
        const { buildingId } = event.payload;
        const building = this.state.buildings.find(b => b.id === buildingId);
        if (building) {
            building.level += 1;
        }
    }
    applyBuildDemolished(event) {
        const { buildingId } = event.payload;
        const idx = this.state.buildings.findIndex(b => b.id === buildingId);
        if (idx === -1)
            return;
        const building = this.state.buildings[idx];
        // Free grid cells
        for (let dy = 0; dy < building.height; dy++) {
            for (let dx = 0; dx < building.width; dx++) {
                const row = this.state.grid.layout[building.y + dy];
                if (row) {
                    this.state.grid.layout[building.y + dy] =
                        row.substring(0, building.x + dx) + '#' + row.substring(building.x + dx + 1);
                }
            }
        }
        this.state.buildings.splice(idx, 1);
    }
    applyBuildToggled(event) {
        const { buildingId } = event.payload;
        const building = this.state.buildings.find(b => b.id === buildingId);
        if (building) {
            building.isEnabled = !building.isEnabled;
        }
    }
    applyHullExpanded(event) {
        const { blocks } = event.payload;
        for (const { x, y } of blocks) {
            if (y >= 0 && y < this.state.grid.layout.length) {
                const row = this.state.grid.layout[y];
                if (x >= 0 && x < row.length) {
                    this.state.grid.layout[y] =
                        row.substring(0, x) + '#' + row.substring(x + 1);
                }
            }
        }
        // Update dimensions if expanded beyond current
        const maxX = Math.max(...blocks.map(b => b.x)) + 1;
        const maxY = Math.max(...blocks.map(b => b.y)) + 1;
        if (maxX > this.state.grid.width)
            this.state.grid.width = maxX;
        if (maxY > this.state.grid.height)
            this.state.grid.height = maxY;
    }
    applyMinibotBuilt(event) {
        const { x, y } = event.payload;
        const minibot = {
            id: this.genId('mb'),
            x,
            y,
            state: 'idle',
            path: [],
            carrying: null,
            targetBuildingId: null,
        };
        this.state.layers.transport.minibots.push(minibot);
        this.state.minibots.push(minibot);
    }
    applyCrossBoardEvent(event) {
        const cbe = event.payload;
        // Add to pending events — will be applied when arrivalTick is reached
        this.state.pendingEvents.push(cbe);
    }
    // Process pending cross-board events that have arrived
    processPendingEvents() {
        const currentTick = this.state.tick;
        const arrived = this.state.pendingEvents.filter(e => e.arrivalTick <= currentTick);
        this.state.pendingEvents = this.state.pendingEvents.filter(e => e.arrivalTick > currentTick);
        for (const event of arrived) {
            // Cross-board events carry resources — add to a storage or building
            if (event.payload) {
                const { resource, amount, targetBuildingId } = event.payload;
                if (targetBuildingId) {
                    const building = this.state.buildings.find(b => b.id === targetBuildingId);
                    if (building) {
                        building.contains[resource] = (building.contains[resource] || 0) + amount;
                    }
                }
            }
        }
    }
    // Advance one tick — run all layers in order
    tick() {
        this.state.tick += 1;
        // Process pending cross-board events first
        this.processPendingEvents();
        // Run each layer if its tick rate matches
        for (const layer of this.layers) {
            if (this.state.tick % layer.tickRate === 0) {
                this.state = layer.execute(this.state, this.state.tick);
            }
        }
    }
    // Get a snapshot of the current state (deep clone)
    getSnapshot() {
        return JSON.parse(JSON.stringify(this.state));
    }
    // Restore from a snapshot
    restoreFromSnapshot(snapshot) {
        this.state = JSON.parse(JSON.stringify(snapshot));
    }
}
exports.Board = Board;



//////////////////
// WEBPACK FOOTER
// ../engine/dist/Board.js
// module id = bzO+
// module chunks = 0